import queryString from 'query-string';

import API_URLS from '@Constants/apiUrls.constants';
import { CommonConstants } from '@Constants/index';
import { GetLists } from '@Models/common.models';
import {
  GroupAdd,
  GroupEdit,
  GroupResponse,
  GroupsListResponse,
} from '@Models/groups.models';
import { GlobalScriptResponse } from '@Models/scripts.models';
import { baseApi } from '@Services/baseApi.service';
import query from '@Utils/common/query.utils';

export const groupsApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['Groups', 'Group', 'GroupsAll', 'GroupTemplates'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getGroupsList: builder.query<GroupsListResponse, GetLists>({
        query: ({ data: { direction, ...resData } }) =>
          `${API_URLS.GROUPS}?${
            direction || CommonConstants.INITIAL_SORT_DIRECTION
          }&${query.stringify({ ...resData })}`,
        providesTags: ['Groups'],
      }),
      getAllGroups: builder.query<GroupResponse[], void>({
        query: () => `${API_URLS.GROUPS}/all`,
        providesTags: ['GroupsAll'],
      }),
      getGroupTemplates: builder.query<
        GlobalScriptResponse[],
        { id: string; type: string }
      >({
        query: ({ id, type }) =>
          `${API_URLS.GROUPS}/${id}/templates?type=${type}`,
        providesTags: ['GroupTemplates'],
      }),
      getGroup: builder.query<GroupResponse, string>({
        query: (id) => `${API_URLS.GROUPS}/${id}`,
        providesTags: ['Group'],
      }),
      addGroup: builder.mutation<GroupResponse, GroupAdd>({
        query: (body) => ({
          url: `${API_URLS.GROUPS}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Groups', 'GroupsAll'],
      }),
      editGroup: builder.mutation<GroupResponse, GroupEdit>({
        query: (body) => ({
          url: `${API_URLS.GROUPS}/${body.id}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['Groups', 'Group', 'GroupsAll'],
      }),
      deleteGroup: builder.mutation<void, string>({
        query: (id) => ({
          url: `${API_URLS.GROUPS}/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Groups', 'Group'],
      }),
      deleteGroups: builder.mutation<void, readonly string[]>({
        query: (ids) => ({
          url: `${API_URLS.GROUPS}?${queryString.stringify(
            { ids },
            { arrayFormat: 'bracket' }
          )}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Groups', 'Group', 'GroupsAll'],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetGroupsListQuery,
  useGetAllGroupsQuery,
  useGetGroupQuery,
  useGetGroupTemplatesQuery,
  useAddGroupMutation,
  useEditGroupMutation,
  useDeleteGroupMutation,
  useDeleteGroupsMutation,
} = groupsApi;
